import { useEffect, useState } from "react";

import {
  Button,
  Checkbox,
  Combobox,
  InfoModal,
  Input,
  LoadingSpinner,
  Toast,
} from "@newsela/angelou";
import { isEmpty } from "@s-libs/micro-dash";
import { useQuery } from "@tanstack/react-query";
import PropTypes from "prop-types";

import {
  withTrackEvent,
  actionNames,
  actionPrefixes,
  objectTypes,
} from "@newsela/snitch";
import globalToasterRef from "@/base/globalToasterRef";
import useClassroomCreate from "@/classrooms/useClassroomCreate";

import {
  allSubjects,
  allSubjectsSlugs,
  ENGLISH_LANGUAGE_ARTS,
  SOCIAL_STUDIES,
} from "@/subjects/subjects.constants";
import useUser from "@/users/useUser";
import { teacherQueries } from "@/teacher";
import ImportSection from "./ImportSection";
import {
  checkboxLabelTextCss,
  checkboxRootCss,
  comboboxCss,
  formContainerCss,
  formItemCss,
  formRowCss,
  modalContentWrapperCss,
  modalFooterButtonRowCss,
  modalTitleCss,
  nameInputLabelCss,
  scrollableBodyCss,
  sectionHeadingTextCss,
  scrollableBodyDropdownVisibleCss,
  footerCss,
} from "./CreateNewClassroomModal.style";

const propTypes = {
  /**
   * Props passed directly to the InfoModal component. See the Angelou InfoModal for more information.
   */
  createClassroomModalProps: PropTypes.object,
  /**
   * Function to fire when a user selects the cancel button.
   */
  handleCancel: PropTypes.func,
  /**
   * Function to fire when a user completes the classroom creation form and selects the submit button.
   */
  submitCallback: PropTypes.func,
  /**
   * Object to pass with additional styles to the modal. See the Angelou InfoModal for more information.
   */
  __cssFor: PropTypes.object,
  /**
   * Disable action buttons.
   */
  isExternalActionLoading: PropTypes.bool,
  hideImportSection: PropTypes.bool,
};

const defaultProps = {
  __cssFor: {},
};

const ButtonWithTrackEvent = withTrackEvent(Button);

const gradeOptions = ["KG"];
for (let grade = 1; grade < 13; grade++) {
  gradeOptions.push(`${grade}`);
}

const EVENT_TOAST_CREATION_FAIL = {
  showIcon: true,
  statusColor: Toast.statusColor.danger,
  children: "Class creation failed",
};

const ELEMENTARY_GRADE = 5;
const SCHOOL_INDEX = 0;

const CreateNewClassroomModal = (props) => {
  const {
    createClassroomModalProps,
    isExternalActionLoading,
    handleCancel,
    submitCallback,
    __cssFor,
  } = props;
  const { isActive } = createClassroomModalProps;

  const { data: user } = useUser();
  const { data: teacherSchools, isLoading: isLoadingSchools } = useQuery({
    ...teacherQueries.schools(),
    enabled: isActive,
  });

  const [postClassroom, { loading: isSaving }] = useClassroomCreate();

  const [name, setName] = useState("");
  const [nameTouched, setNameTouched] = useState(false);

  const [grade, setGrade] = useState("");
  const [gradeTouched, setGradeTouched] = useState(false);

  const [subject, setSubject] = useState("");
  const [subjectTouched, setSubjectTouched] = useState(false);

  const [school, setSchool] = useState(teacherSchools?.[SCHOOL_INDEX] ?? {});
  const [schoolTouched, setSchoolTouched] = useState(false);

  const [isElementary, setIsElementary] = useState(false);

  const isLoading = isLoadingSchools || isSaving || isExternalActionLoading;

  useEffect(() => {
    setSchool((current) =>
      current.id || !teacherSchools?.length ? current : teacherSchools[0],
    );
  }, [teacherSchools]);

  const validationErrors = {
    name: "Please add a classroom name",
    grade: "Please select a grade",
    subject: "Please select a subject",
    school: "Please select a school",
  };

  const slugifySubjectString = (subject) => {
    if (subject === ENGLISH_LANGUAGE_ARTS) {
      return allSubjectsSlugs.ENGLISH_LANGUAGE_ARTS;
    }

    if (subject === SOCIAL_STUDIES) {
      return allSubjectsSlugs.SOCIAL_STUDIES;
    }

    return allSubjectsSlugs[subject?.toUpperCase().split(" ").join("_")];
  };

  const transformKGGrade = (grade) => (grade === "KG" ? 0 : grade);

  const isElementaryGrade = (grade) =>
    transformKGGrade(grade) <= ELEMENTARY_GRADE;

  const classroomPayload = {
    name,
    grade: transformKGGrade(grade),
    subject_slugs: [slugifySubjectString(subject)],
    is_all_ages: !isElementary,
    school: school?.unique_id,
  };

  const resetAllFields = () => {
    setName("");
    setGrade("");
    setSubject("");
    setSchool(user.teacher.school);
    setNameTouched(false);
    setGradeTouched(false);
    setSubjectTouched(false);
    setSchoolTouched(false);
  };

  const touchAllFields = () => {
    setNameTouched(true);
    setGradeTouched(true);
    setSubjectTouched(true);
    setSchoolTouched(true);
  };

  const postClassroomData = async (payload) => {
    try {
      /**
       * touchAllFields call is needed in case a user tries to submit the form without interacting with the fields.
       */
      touchAllFields();
      if (name && grade && subject && !isEmpty(school)) {
        const { data: classroomResponseObj } = await postClassroom(payload);
        submitCallback(classroomResponseObj);
        resetAllFields();
      }
    } catch {
      globalToasterRef.current.displayToast(EVENT_TOAST_CREATION_FAIL);
    }
  };

  const renderContent = () => {
    if (isLoading)
      return (
        <LoadingSpinner dataProps={{ "data-testid": "loading-spinner" }} />
      );
    return (
      <>
        <h2 css={sectionHeadingTextCss}>Complete the class's new profile.</h2>
        <form>
          <div css={formContainerCss}>
            <div css={formRowCss}>
              <Input
                label="Class Name"
                onChange={(e) => {
                  setName(e.target.value);
                }}
                value={name}
                error={nameTouched && name === "" && validationErrors.name}
                onBlur={() => {
                  if (!nameTouched) {
                    setNameTouched(true);
                  }
                }}
                touched={nameTouched}
                __cssFor={{ root: formItemCss, label: nameInputLabelCss }}
              />
              <Combobox
                items={gradeOptions}
                label="Grade"
                onSelectedItemChange={(e) => {
                  const { selectedItem } = e;
                  if (isElementaryGrade(selectedItem)) {
                    setIsElementary(true);
                  }
                  setGrade(selectedItem);
                }}
                selectedItem={grade}
                error={gradeTouched && !grade && validationErrors.grade}
                createInputProps={() => ({
                  onBlur: () => {
                    if (!gradeTouched) {
                      setGradeTouched(true);
                    }
                  },
                })}
                touched={gradeTouched}
                __cssFor={{
                  root: [comboboxCss, formItemCss],
                }}
              />
            </div>
            <div css={formRowCss}>
              <Combobox
                items={allSubjects}
                label="Subject"
                onSelectedItemChange={(e) => {
                  setSubject(e.selectedItem);
                }}
                selectedItem={subject}
                error={subjectTouched && !subject && validationErrors.subject}
                createInputProps={() => ({
                  onBlur: () => {
                    if (!subjectTouched) {
                      setSubjectTouched(true);
                    }
                  },
                })}
                touched={subjectTouched}
                __cssFor={{
                  root: [comboboxCss, formItemCss],
                }}
              />
              <Combobox
                items={teacherSchools}
                label="School"
                itemToString={(item) => item?.name || ""}
                onSelectedItemChange={(e) => setSchool(e.selectedItem || {})}
                selectedItem={school}
                error={
                  schoolTouched && isEmpty(school) && validationErrors.school
                }
                createInputProps={() => ({
                  onBlur: () => {
                    if (!schoolTouched) {
                      setSchoolTouched(true);
                    }
                  },
                })}
                touched={schoolTouched}
                __cssFor={{
                  root: [comboboxCss, formItemCss],
                }}
              />
            </div>
          </div>
          <Checkbox
            label="Elementary Content Filter"
            value={isElementary}
            checked={isElementary}
            disabled={isElementaryGrade(grade)}
            onChange={(e) => setIsElementary(e.target.checked)}
            __cssFor={{
              root: checkboxRootCss,
              labelText: checkboxLabelTextCss,
            }}
          />
        </form>
        {!props.hideImportSection && <ImportSection />}
      </>
    );
  };

  return (
    <InfoModal
      labelId="CreateClass"
      title="Add class"
      onClose={resetAllFields}
      footer={
        <div css={modalFooterButtonRowCss}>
          <ButtonWithTrackEvent
            flavor={Button.flavors.secondary}
            onClick={handleCancel}
            objectType={objectTypes.classroom}
            actionPrefix={actionPrefixes.cancel}
            actionName={actionNames.create}
            streamProperties={{
              description: "Cancel classroom creation",
            }}
          >
            Cancel
          </ButtonWithTrackEvent>
          <ButtonWithTrackEvent
            flavor={Button.flavors.primary}
            onClick={() => {
              postClassroomData(classroomPayload);
            }}
            type="submit"
            objectType={objectTypes.classroom}
            actionName={actionNames.create}
            streamProperties={{
              description: "Create classroom",
            }}
            disabled={isLoading}
          >
            Create & add class
          </ButtonWithTrackEvent>
        </div>
      }
      {...createClassroomModalProps}
      __cssFor={{
        ...__cssFor,
        title: modalTitleCss,
        scrollableBody: props.hideImportSection
          ? scrollableBodyDropdownVisibleCss
          : scrollableBodyCss,
        modalContentWrapper: {
          ...modalContentWrapperCss,
          ...__cssFor.modalContentWrapperCss,
        },
        root: { zIndex: "15" }, // any value 15 or greater, to surpass global header's z-index
        footer: footerCss,
      }}
    >
      {renderContent()}
    </InfoModal>
  );
};

CreateNewClassroomModal.propTypes = propTypes;
CreateNewClassroomModal.defaultProps = defaultProps;

export default CreateNewClassroomModal;
